import { validateTemplateLayout } from "@/lib/template-layout/validateTemplateLayout";
import {
  serializeTemplateStudioDraft,
  type TemplateStudioDraft,
} from "@/lib/template-studio/draft/templateStudioDraft";
import type { TemplateLayoutRepositoryError } from "@/lib/template-studio/repository/templateLayoutRepository.types";
import type {
  TemplateLayout,
  TemplateLayoutValidationError,
} from "@/types/template-layout/templateLayout.types";

export interface TemplateStudioSaveMetadata {
  name: string;
  description?: string;
}

export interface TemplateStudioSavedTemplateRecord {
  id: string;
  name: string;
  description?: string;
  updatedAt: string;
  savedDocumentSnapshot: string;
}

export type TemplateStudioSaveError =
  | {
      kind: "validation";
      message: string;
      validationErrors: TemplateLayoutValidationError[];
    }
  | { kind: "request"; message: string }
  | { kind: "in-use"; message: string }
  | {
      kind: "conflict";
      message: string;
      currentUpdatedAt?: string;
    };

export interface TemplateStudioSaveState {
  savedTemplate: TemplateStudioSavedTemplateRecord | null;
  isDirty: boolean;
  isSaving: boolean;
  lastSavedAt: string | null;
  error: TemplateStudioSaveError | null;
}

/** @public */
export const TEMPLATE_STUDIO_SAVE_ERROR_MESSAGE: Record<
  TemplateStudioSaveError["kind"],
  string
> = {
  validation: "Fix the highlighted template issues and try again.",
  request: "Could not save the template. Try again.",
  "in-use":
    "Briefings now use this template, so it can't be changed. Save your changes as a copy instead.",
  conflict:
    "This template was updated elsewhere. Reload the latest version, or save your changes as a copy.",
};

/** Errors that block updating the saved template; the draft can still be saved as a copy. */
export const isTemplateStudioSaveAsCopyError = (
  error: TemplateStudioSaveError | null,
): boolean => error?.kind === "conflict" || error?.kind === "in-use";

/** @public */
export const createInitialTemplateStudioSaveState =
  (): TemplateStudioSaveState => ({
    savedTemplate: null,
    isDirty: false,
    isSaving: false,
    lastSavedAt: null,
    error: null,
  });

const mapTemplateLayoutRepositoryErrorToSaveError = (
  error: TemplateLayoutRepositoryError,
): TemplateStudioSaveError => {
  switch (error.code) {
    case "INVALID_DOCUMENT":
      return {
        kind: "validation",
        message: error.message,
        validationErrors: error.validationErrors,
      };
    case "STALE_TEMPLATE":
      return {
        kind: "conflict",
        message: error.message,
        ...(error.currentUpdatedAt !== undefined
          ? { currentUpdatedAt: error.currentUpdatedAt }
          : {}),
      };
    case "TEMPLATE_IN_USE":
      return { kind: "in-use", message: error.message };
    default:
      return {
        kind: "request",
        message: error.message,
      };
  }
};

const serializeComparableTemplateStudioDraft = (
  draft: TemplateStudioDraft,
): string => {
  const result = validateTemplateLayout(draft);
  return serializeTemplateStudioDraft(result.success ? result.data : draft);
};

/** @public */
export const resolveTemplateStudioSaveMetadata = (
  state: TemplateStudioSaveState,
): TemplateStudioSaveMetadata => ({
  name: state.savedTemplate?.name ?? "",
  description: state.savedTemplate?.description,
});

/** @public */
export const resolveTemplateStudioSaveErrorMessage = (
  error: TemplateStudioSaveError,
): string => {
  if (error.kind === "validation" && error.validationErrors.length > 0) {
    return error.validationErrors.map((entry) => entry.message).join("; ");
  }

  return TEMPLATE_STUDIO_SAVE_ERROR_MESSAGE[error.kind];
};

/** @public */
export const canBeginTemplateStudioSave = (
  state: TemplateStudioSaveState,
): boolean => !state.isSaving;

/** @public */
export const beginTemplateStudioSave = (
  state: TemplateStudioSaveState,
): TemplateStudioSaveState => {
  if (state.isSaving) return state;

  return {
    ...state,
    isSaving: true,
    error: null,
  };
};

/**
 * Builds the saved-template record that Save updates, from a persisted layout.
 * @public
 */
export const createTemplateStudioSavedTemplateRecord = (
  layout: TemplateLayout,
): TemplateStudioSavedTemplateRecord => ({
  id: layout.id,
  name: layout.name,
  description: layout.description,
  updatedAt: layout.updatedAt,
  savedDocumentSnapshot: serializeComparableTemplateStudioDraft(
    layout.document,
  ),
});

/** @public */
export const completeTemplateStudioSaveSuccess = (
  state: TemplateStudioSaveState,
  layout: TemplateLayout,
  currentDraft: TemplateStudioDraft,
): TemplateStudioSaveState => {
  const savedTemplate = createTemplateStudioSavedTemplateRecord(layout);

  return {
    ...state,
    isSaving: false,
    isDirty:
      serializeComparableTemplateStudioDraft(currentDraft) !==
      savedTemplate.savedDocumentSnapshot,
    error: null,
    lastSavedAt: layout.updatedAt,
    savedTemplate,
  };
};

/** @public */
export const failTemplateStudioSave = (
  state: TemplateStudioSaveState,
  error: TemplateLayoutRepositoryError,
): TemplateStudioSaveState & { error: TemplateStudioSaveError } => ({
  ...state,
  isSaving: false,
  error: mapTemplateLayoutRepositoryErrorToSaveError(error),
});

/**
 * Recomputes dirty state from the current draft; clears save errors after edits.
 * @public
 */
export const syncTemplateStudioSaveDirtyState = (
  state: TemplateStudioSaveState,
  draft: TemplateStudioDraft | null,
): TemplateStudioSaveState => {
  if (draft == null) return createInitialTemplateStudioSaveState();

  if (state.savedTemplate == null) {
    return {
      ...state,
      isDirty: true,
      error: null,
    };
  }

  const isDirty =
    serializeComparableTemplateStudioDraft(draft) !==
    state.savedTemplate.savedDocumentSnapshot;

  return {
    ...state,
    isDirty,
    error:
      isDirty && !isTemplateStudioSaveAsCopyError(state.error)
        ? null
        : state.error,
  };
};
